// Keep, Remove… and Approve & send on one or more pre-leads, shared by the bulk bar, the drawer and the keyboard.
// Approve & send is the only way a first email leaves (send_first on the outreach function).
import { Loader2, X } from "lucide-react";
import {
  useDataProvider,
  useGetIdentity,
  useNotify,
  useRefresh,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useContext, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

import type { CrmDataProvider } from "../../providers/types";
import { useConfigurationContext } from "../../root/ConfigurationContext";
import { useDefaultSender } from "../leads/useDefaultSender";
import { PreLeadBusyContext } from "./preLeadBusy";
import { keepPreLead, removePreLeads } from "./preLeads";
import type { PreLead } from "./types";

/** A converted or closed row keeps its outcome (bounced, unsubscribed…) */
export const isRemovable = (r: PreLead) =>
  r.status !== "converted" && r.status !== "closed";

export const usePreLeadActions = (onDone?: () => void) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const config = useConfigurationContext();
  const defaultSender = useDefaultSender();
  const { identity } = useGetIdentity();
  const { setBusy } = useContext(PreLeadBusyContext);
  const [working, setWorking] = useState(false);
  const [removing, setRemoving] = useState<PreLead[] | null>(null);
  const [reason, setReason] = useState("");

  const mark = (id: Identifier, on: boolean) =>
    setBusy((busy) => {
      const next = new Set(busy);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  /** One row after another (each keep is 10 to 40 s of look-ups and AI); a failure is told and the rest go on */
  const each = async (
    rows: PreLead[],
    act: (row: PreLead) => Promise<void>,
  ) => {
    setWorking(true);
    for (const row of rows) {
      mark(row.id, true);
      try {
        await act(row);
      } catch (error) {
        notify("resources.pre_leads.notify.failed", {
          type: "error",
          messageArgs: {
            name: row.company_name,
            error: (error as Error).message,
          },
        });
      } finally {
        mark(row.id, false);
      }
    }
    setWorking(false);
    onDone?.();
    refresh();
  };

  const keep = (rows: PreLead[]) =>
    each(rows, async (row) => {
      const next = await keepPreLead(dataProvider, row, {
        config,
        identity: {
          fullName: identity?.fullName ?? "",
          email: String(identity?.email ?? ""),
        },
        defaultSender,
        offer: String(row.segment?.offer ?? ""),
      });
      const log = (next.evidence?.log as string[] | undefined) ?? [];
      if (next.status === "ready")
        notify("resources.pre_leads.notify.ready", {
          type: "success",
          messageArgs: { name: row.company_name },
        });
      else if (next.status === "closed")
        notify("resources.pre_leads.notify.no_why_now", {
          type: "info",
          messageArgs: { name: row.company_name },
        });
      else
        notify("resources.pre_leads.notify.kept", {
          type: "warning",
          messageArgs: { name: row.company_name, error: log.at(-1) ?? "" },
        });
    });

  const approve = (rows: PreLead[]) =>
    each(rows, async (row) => {
      const result = await dataProvider.outreach<{
        sent_at?: string;
        queued_until?: string;
      }>({ action: "send_first", pre_lead_id: row.id });
      notify(
        result.queued_until
          ? "resources.pre_leads.notify.queued"
          : "resources.pre_leads.notify.sent",
        {
          type: "success",
          messageArgs: {
            name: row.company_name,
            date: result.queued_until
              ? new Date(result.queued_until).toLocaleString()
              : "",
          },
        },
      );
    });

  const remove = async () => {
    if (!removing) return;
    setWorking(true);
    try {
      await removePreLeads(dataProvider, removing, reason);
      notify("resources.pre_leads.notify.removed", {
        type: "info",
        messageArgs: { smart_count: removing.length },
      });
      setRemoving(null);
      setReason("");
      onDone?.();
      refresh();
    } catch (error) {
      notify((error as Error).message, { type: "error" });
    } finally {
      setWorking(false);
    }
  };

  const removeDialog = (
    <Dialog
      open={removing != null}
      onOpenChange={(open) => !open && setRemoving(null)}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {translate("resources.pre_leads.remove_title", {
              smart_count: removing?.length ?? 0,
            })}
          </DialogTitle>
        </DialogHeader>
        <Input
          aria-label={translate("resources.pre_leads.remove_reason")}
          placeholder={translate("resources.pre_leads.remove_reason")}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !working && remove()}
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => setRemoving(null)}>
            {translate("ra.action.cancel")}
          </Button>
          <Button disabled={working} onClick={remove}>
            {working ? <Loader2 className="animate-spin" /> : <X />}
            {translate("resources.pre_leads.bulk.remove")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return {
    working,
    removing: removing != null,
    keep,
    approve,
    askRemove: (rows: PreLead[]) => {
      if (rows.length) setRemoving(rows);
    },
    removeDialog,
  };
};
